import { Heading, UnstyledButton } from "@sidelinesports/fan-design-system";
import { BingoIcon } from "@sidelinesports/icons";
import {
  type ComponentProps,
  type ReactElement,
  useCallback,
  useRef,
} from "react";
import { useKey } from "react-use";
import type { Handler } from "react-use/lib/useKey";
import styles from "./MenuItem.module.css";
import { useSlideoutMenuContext } from "./SlideoutMenu.tsx";

export const MenuItem = ({
  icon = <BingoIcon />,
  children = "",
  name = children,
  onClick,
  ...props
}: {
  icon?: ReactElement;
  onClick?: Handler;
  name?: string;
  children: string;
} & Omit<ComponentProps<"button">, "children">) => {
  const ref = useRef(null);
  const context = useSlideoutMenuContext();

  const handleClick = useCallback(
    (e: KeyboardEvent) => {
      // When a user clicks on one of the menu items, close the menu before
      // executing the onClick handler.
      context.onClose();
      onClick?.(e);
    },
    [context, onClick],
  );

  useKey("Enter", handleClick, { target: ref.current });
  useKey(" ", handleClick, { target: ref.current });

  return (
    <UnstyledButton
      aria-label={name}
      className={styles["menu-item-box"]}
      onClick={handleClick as unknown as (e: React.MouseEvent) => void}
      role="menuitem"
      ref={ref}
      {...props}
    >
      <span className={styles["menu-item-link"]}>
        {icon}
        <Heading as="span" level={4}>
          {children}
        </Heading>
      </span>
    </UnstyledButton>
  );
};
